/**
 * Maps Figma / UI labels to AEAT BFF `flow` values.
 * Source: docs/AEAT_SIGN_IN_METHOD_MAPPING.md
 */

export const AEAT_METHOD_OPTION_CERTIFICATE = 'Digital Certificate / DNIe';
/** Same AEAT option as {@link AEAT_METHOD_OPTION_CERTIFICATE} — `screens.json` / `chunk_01.json` ES copy. */
export const AEAT_METHOD_OPTION_CERTIFICATE_ES = 'Certificado digital / DNIe';
/** UX copy (`screens.json` option 0) — must be recognised when stripping hidden certificate row. */
export const AEAT_METHOD_OPTION_CERTIFICATE_UX_EN =
  'Digital certificate or electronic ID (DNIe)';
/** Spanish UX copy for the same option. */
export const AEAT_METHOD_OPTION_CERTIFICATE_UX_ES =
  'Certificado digital o DNI electrónico (DNIe)';

export function isAeatCertificateOptionLabel(label: string): boolean {
  const t = String(label).trim();
  return (
    t === AEAT_METHOD_OPTION_CERTIFICATE ||
    t === AEAT_METHOD_OPTION_CERTIFICATE_ES ||
    t === AEAT_METHOD_OPTION_CERTIFICATE_UX_EN ||
    t === AEAT_METHOD_OPTION_CERTIFICATE_UX_ES
  );
}
export const AEAT_METHOD_OPTION_CLAVE = 'Cl@ve PIN / Cl@ve Móvil';
export const AEAT_METHOD_OPTION_REFERENCE = 'Número de referencia';
/** Hidden in v1 — not supported by datos fiscales auth PDF */
export const AEAT_METHOD_OPTION_IBAN = 'IBAN (if no prior return filed)';

/** Manual path: user uploads documents instead of live AEAT login on this step */
export const AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN = 'Upload ID & last tax return';

/** Cl@ve, reference, and certificate require NIF (or cert-derived NIF) before live AEAT connect. */
export function isAeatMethodUploadOrIbanNoLiveConnect(label: string): boolean {
  const t = label.trim();
  return t === AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN || t === AEAT_METHOD_OPTION_IBAN;
}

/** InnoTaxes page for certificates & official filings (linked from “Need help getting access?”). */
export const AEAT_ACCESS_HELP_URL =
  'https://www.innotaxes.com/tax-services-spain/certificates-official-filings-package';

const AEAT_ACCESS_HELP_OPTION_NORMALIZED = 'need help getting access?';

export function isAeatAccessHelpOptionLabel(label: string): boolean {
  return label.trim().toLowerCase() === AEAT_ACCESS_HELP_OPTION_NORMALIZED;
}

/** Short hint lines for the info popover beside each AEAT sign-in option. */
export function getAeatMethodHelpLines(label: string): string[] | null {
  const t = label.trim();
  if (isAeatAccessHelpOptionLabel(t)) return null;
  if (t === AEAT_METHOD_OPTION_CLAVE) {
    return ['Log in using Spain’s official identity app.'];
  }
  if (t === AEAT_METHOD_OPTION_REFERENCE) {
    return [
      'Enter the reference number generated by AEAT to access your tax data without certificates.',
    ];
  }
  if (t === AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN) {
    return [
      'Upload your ID (front and back) and last year’s tax return.',
      'If you didn’t file last year, just provide the last 5 digits of your IBAN.',
    ];
  }
  if (t === AEAT_METHOD_OPTION_IBAN) {
    return [
      'If you didn’t file taxes last year, AEAT can verify your identity using the last 5 digits of your IBAN.',
    ];
  }
  if (t === AEAT_METHOD_OPTION_CERTIFICATE) {
    return ['Connect instantly using your official digital certificate.'];
  }
  return null;
}

export function getAeatMethodOptionEmoji(label: string): string | null {
  const t = label.trim();
  if (isAeatAccessHelpOptionLabel(t)) return null;
  const map: Record<string, string> = {
    [AEAT_METHOD_OPTION_CLAVE]: '\u{1F4F1}',
    [AEAT_METHOD_OPTION_REFERENCE]: '\u{1F522}',
    [AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN]: '\u{1FAAA}',
    [AEAT_METHOD_OPTION_IBAN]: '\u{1F3DB}\uFE0F',
    [AEAT_METHOD_OPTION_CERTIFICATE]: '\u{1F510}',
  };
  return map[t] ?? null;
}

export function openAeatAccessHelpInNewTab(): void {
  window.open(AEAT_ACCESS_HELP_URL, '_blank', 'noopener,noreferrer');
}

/** Exact UI label for the help row (matches i18n `screens.json`). */
export const AEAT_METHOD_OPTION_NEED_HELP = 'Need help getting access?';

/** Full Figma / flow option order for `sign_in_to_aeat` (radio list). */
export const AEAT_SIGN_IN_OPTIONS_FULL = [
  // AEAT_METHOD_OPTION_CERTIFICATE, // TODO: restore when cert-login flow is stable
  AEAT_METHOD_OPTION_CLAVE,
  AEAT_METHOD_OPTION_REFERENCE,
  AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN,
  AEAT_METHOD_OPTION_IBAN,
  AEAT_METHOD_OPTION_NEED_HELP,
] as const;

/** Temporary: strip certificate from API/i18n option lists (same as commented row above). */
export function withoutAeatCertificateOption(options: string[]): string[] {
  return options.filter((o) => !isAeatCertificateOptionLabel(o));
}

/** English labels in `AEAT_SIGN_IN_OPTIONS_FULL` order (without certificate). */
export function aeatSignInCanonicalOptions(): string[] {
  return withoutAeatCertificateOption([...AEAT_SIGN_IN_OPTIONS_FULL]);
}

/**
 * Map a displayed (possibly translated) option label to the canonical English label
 * used for BFF + comparisons. Relies on option order matching `AEAT_SIGN_IN_OPTIONS_FULL`.
 */
export function resolveCanonicalAeatMethod(stored: string, displayOptions: string[]): string {
  const canonicalList = aeatSignInCanonicalOptions();
  const s = (stored || '').trim();
  if (!s) return '';
  // Certificate is hidden in v1 — normalize ES/EN saved values so we don't mismatch canonical rows.
  if (isAeatCertificateOptionLabel(s)) return '';
  if (canonicalList.includes(s)) return s;
  if (displayOptions.length === canonicalList.length) {
    const idx = displayOptions.findIndex((x) => String(x).trim() === s);
    if (idx >= 0 && canonicalList[idx]) return canonicalList[idx];
  }
  return s;
}

/** @deprecated Prefer {@link AEAT_SIGN_IN_OPTIONS_FULL}; kept for older imports. */
export const AEAT_SIGN_IN_OPTIONS_V1 = [
  AEAT_METHOD_OPTION_CLAVE,
  AEAT_METHOD_OPTION_REFERENCE,
] as const;

export type AeatBffFlow = 'certificate' | 'clave_movil' | 'reference';

/** AEAT fiscal-data `pdp`: S = fiscal + personal data (product default; UI choice removed). */
export const AEAT_DEFAULT_PDP = 'S';

export function uiLabelToBffFlow(label: string): AeatBffFlow {
  const t = label.trim();
  if (isAeatCertificateOptionLabel(t)) return 'certificate';
  if (t === AEAT_METHOD_OPTION_CLAVE) return 'clave_movil';
  if (t === AEAT_METHOD_OPTION_REFERENCE) return 'reference';
  throw new Error(`Unknown AEAT sign-in method: ${label}`);
}

export const CERTIFICATE_WEB_COPY =
  'Upload your PKCS#12 file (.p12 or .pfx) from your machine and its password. Our server uses it only for HTTPS client authentication (mTLS) to AEAT; the password is not stored. You will be asked for the password again when requesting fiscal data.';

/** Shown when API returns code FISCAL_NOT_AUTHORIZED (e.g. AEAT error 1310). */
export const FISCAL_NOT_AUTHORIZED_HINT =
  'AEAT rejected access to this procedure. For the certificate flow, the NIF you enter must match the identity in your PKCS#12 (same person or valid representation). Check ejercicio and certificate.';

/** Shown when fiscal-data returns Error 5001 (domicilio not ratified). */
export const RATIFICATION_REQUIRED_FILE_HINT =
  'AEAT will not release tax data until your fiscal domicile (domicilio fiscal) is ratified for this case. Use the official AEAT links shown below to complete ratification, then return here and choose “I completed ratification — check again”. If you already ratified, wait for AEAT to update or confirm the NIF and tax year (ejercicio) match what you used on AEAT.';

/** DNS / network: API cannot reach AEAT (HTTP 503, code AEAT_TRANSPORT). */
export const AEAT_TRANSPORT_HINT =
  'The API could not reach AEAT (network/DNS or timeout). The server responds with HTTP 503 for this. Fix outbound HTTPS from the API host, or set MOCK_EXTERNAL_APIS=true on the API for local testing without live AEAT.';
